<template>
  <div class="rotashift-agenda">
    <div v-if="getShiftAsignmentType(shift) === 'holiday'" class="title">
      <font-awesome-icon icon="umbrella-beach" fixed-width class="me-1" />
      <span> On Holiday </span>
    </div>
    <div v-if="getShiftAsignmentType(shift) === 'sick'" class="title">
      <font-awesome-icon icon="temperature-high" fixed-width class="me-1" />
      <span> Off Sick </span>
    </div>
    <div class="time">
      <font-awesome-icon :icon="['fas', 'clock']" class="me-1" fixed-width />
      <template v-if="rotaTimeStyle({ type: rotaType }) !== 'shift-based'">
        <span>
          {{ formatTimeHoursMins(shift.startDate) }} -
          {{ formatTimeHoursMins(shift.endDate) }}
        </span>
      </template>
      <template v-else>
        <span v-if="getShiftSession(shift) == null">
          {{ getShiftSession(shift) }}
          Custom shift ({{ formatTimeHoursMins(shift.startDate) }} -
          {{ formatTimeHoursMins(shift.endDate) }})
        </span>
        <span v-else>
          {{ getShiftSession(shift).name }} shift ({{
            formatTimeHoursMins(shift.startDate)
          }}
          - {{ formatTimeHoursMins(shift.endDate) }})
        </span>
      </template>
    </div>
    <div
      v-if="shiftCurrentAssignmentForUser.selfAssigned"
      class="self-assigned"
    >
      <font-awesome-icon fixed-width :icon="['far', 'hand-paper']" />
      <span>
        This shift was self-assigned on
        {{ formatModifiedDateLong(shiftCurrentAssignmentForUser.selfAssigned) }}
      </span>
    </div>
    <button
      v-if="
        getShiftAsignmentType(shift) !== 'current' &&
        hasAssignments &&
        !alwaysExpanded
      "
      class="btn btn-sm btn-outline-primary"
      type="button"
      @click="expanded = !expanded"
    >
      <span v-if="expanded">
        <font-awesome-icon icon="angle-up" fixed-width /> Hide Details
      </span>
      <span v-else>
        <font-awesome-icon icon="angle-down" fixed-width /> Show Details
      </span>
    </button>
    <div v-if="getShiftDuties.length > 0 && (expanded || alwaysExpanded)">
      Duties:&nbsp;
      <span v-for="(role, index) in getShiftDuties" :key="index">
        <span v-if="index > 0">, </span>
        {{ role.name }} <span class="badge bg-success">{{ role.value }}</span>
      </span>
    </div>
    <div v-if="shift.notes && (expanded || alwaysExpanded)" class="mb-2 notes">
      {{ shift.notes }}
    </div>
    <div
      v-if="(showAssignedTo && expanded) || (alwaysExpanded && !hideAssigned)"
      class="mb-2 assigned-to"
    >
      <div>Assigned to</div>
      <div>
        <rota-shift-assignedto-view-for-agenda
          :user-id="userId"
          :shift-id="shift.id"
          :assigned-to="shift.assignedTo ?? []"
        />
      </div>
    </div>
    <div
      v-if="
        hasActivities ||
        hasSupportActivities ||
        (shift.schedule && shift.schedule.length > 0)
      "
      class="d-flex flex-column"
    >
      <!-- rota shift schedule and activities need to be at the same DOM level for css ordering to work -->
      <rota-shift-schedule-view
        v-if="shift.schedule && shift.schedule.length > 0"
        :shift="shift"
      />
      <rota-agenda-shift-activities
        :shift="shift"
        :activities="activities"
        :show-link="true"
        :show-notes="true"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import find from "lodash/find";
import findLast from "lodash/findLast";
import has from "lodash/has";

import { getShiftSession } from "../../../lib/rota/shift-functions";
import {
  formatDateAsDay,
  formatRotaStartDateLong,
  formatTimeHoursMins,
  formatModifiedDateLong,
  rotaTimeStyle,
  formatTimeStringForHtmlInput,
} from "../../../lib/date-functions";
import sessions from "../../../lib/rota/sessions";
import RotaShiftAssignedtoViewForAgenda from "./RotaShiftAssignedtoViewForAgenda.vue";
import RotaAgendaShiftActivities from "./RotaAgendaShiftActivities.vue";
import RotaShiftScheduleView from "../shift/RotaShiftScheduleView.vue";
import config from "../../../config";

const { rotaConfiguration } = config;

export default {
  name: "RotaAgendaShiftView",
  components: {
    RotaShiftAssignedtoViewForAgenda,
    RotaAgendaShiftActivities,
    RotaShiftScheduleView,
  },
  props: {
    userId: {
      type: String,
      default() {
        return null;
      },
    },
    rotaType: {
      type: String,
      default() {
        return "";
      },
    },
    rotaStatus: {
      type: String,
      default() {
        return "";
      },
    },
    shift: {
      type: Object,
      default() {
        return {};
      },
    },
    rotaAgencies: {
      type: Array,
      default() {
        return [];
      },
    },
    activities: {
      type: [Boolean, Array],
      default() {
        return [];
      },
    },
    alwaysExpanded: {
      type: Boolean,
      default() {
        return false;
      },
    },
    hideAssigned: {
      type: Boolean,
      default() {
        return false;
      },
    },
  },
  setup() {
    return {
      findLast,
      sessions,
      getShiftSession,
      rotaTimeStyle,
      formatDateAsDay,
      formatRotaStartDateLong,
      formatTimeHoursMins,
      formatModifiedDateLong,
      formatTimeStringForHtmlInput,
    };
  },
  data() {
    return {
      expanded: true,
      showUsers: false,
    };
  },
  computed: {
    ...mapGetters({
      ccLocations: "locations",
      dutiesQualifiedFor: "dutiesQualifiedFor",
    }),
    hasAssignments() {
      return has(this.shift, "assignedTo");
    },
    hasActivities() {
      return rotaConfiguration[this.rotaType]?.hasActivities || false;
    },
    hasSupportActivities() {
      return rotaConfiguration[this.rotaType]?.hasSupportActivities || false;
    },
    shiftCurrentAssignmentForUser() {
      if (!has(this.shift, "assignedTo")) {
        return {};
      }
      const lastEntry = findLast(this.shift.assignedTo, {
        userId: this.userId,
        type: "current",
      });
      return lastEntry === undefined ? {} : lastEntry;
    },
    getShiftDate() {
      if (this.rotaStatus === "master") {
        return `${formatDateAsDay(this.shift.startDate)}`;
      }
      return `${formatRotaStartDateLong(this.shift.startDate)}`;
    },
    getShiftDuties() {
      const duties = [];
      if (this.shift.dutiesRequired) {
        this.shift.dutiesRequired.forEach((duty) => {
          const dutyObj = find(this.dutiesQualifiedFor, { value: duty });
          if (dutyObj !== undefined) {
            duties.push(dutyObj);
          }
        });
      }
      return duties;
    },
    showAssignedTo() {
      if (this.hideAssigned) {
        return false;
      }
      if (!has(this.shift, "assignedTo")) {
        return false;
      }
      if (
        this.shift.assignedTo.length === 1 &&
        this.shift.assignedTo[0].userId === this.userId
      ) {
        return false;
      }
      return true;
    },
  },
  watch: {
    shift: {
      handler(newData) {
        this.expanded = this.getShiftAsignmentType(newData) === "current";
        if (!has(this.shift, "assignedTo")) {
          this.expanded = true;
        }
        if (this.alwaysExpanded) {
          this.expanded = true;
        }
      },
      immediate: true,
    },
  },
  methods: {
    getShiftAsignmentType(shift) {
      if (!has(shift, "assignedTo")) {
        return "";
      }
      const match = findLast(shift.assignedTo, { userId: this.userId });
      return match ? match.type : "";
    },
  },
};
</script>

<!--
  note, don't put any CSS in here as it causes
  mini-css-extract-plugin warnings on production build
-->
